import { inject, Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { ApiConfig, ENVIRONMENT } from '@agilox/common';
import { AccountInfo, InteractionRequiredAuthError } from '@azure/msal-browser';

@Injectable({
	providedIn: 'root',
})
export class AuthService {
	private env = inject(ENVIRONMENT);
	private msalService = inject(MsalService);

	get msalInstance() {
		return this.msalService;
	}
	constructor() {
		this.msalInstance.initialize();
	}

	/**
	 * Acquire an access token for the API inferred from the target URL using environment.msal.apis baseUrl matching.
	 * If no matching API is found, resolves to an empty string and no Authorization header should be added.
	 */
	async getAccessTokenForUrl(url: string): Promise<string> {
		const api = this.resolveApiConfigForUrl(url);
		if (!api) return '';

		const account = this.getActiveAccount();
		if (!account) throw new Error('No active account');

		try {
			const result = await this.msalInstance.instance.acquireTokenSilent({
				account,
				scopes: api.scopes,
			});
			return result.accessToken;
		} catch (e) {
			if (e instanceof InteractionRequiredAuthError) {
				// Trigger interactive flow; this will redirect and return control later
				await this.msalInstance.instance.acquireTokenRedirect({ scopes: api.scopes });
			}
			throw e;
		}
	}

	/**
	 * Wrapper around window.fetch that automatically attaches a Bearer token
	 * if the URL matches one of the configured MSAL APIs in the environment.
	 */
	async fetchWithAuth(input: string, init: RequestInit = {}): Promise<Response> {
		const token = await this.getAccessTokenForUrl(input);

		const headers = new Headers(init.headers || {});
		if (token) {
			headers.set('Authorization', `Bearer ${token}`);
		}
		return fetch(input, { ...init, headers });
	}

	logout() {
		this.msalInstance.logoutRedirect();
	}

	login() {
		this.msalService.instance.loginRedirect();
	}

	private getActiveAccount(): AccountInfo | null {
		return (
			this.msalInstance.instance.getActiveAccount() ||
			this.msalInstance.instance.getAllAccounts()[0] ||
			null
		);
	}

	private resolveApiConfigForUrl(url: string): ApiConfig | null {
		const apis = this.env.msal?.apis ?? ({} as Record<string, ApiConfig>);
		// Normalize url to pathname when possible
		let target = url;
		try {
			// If absolute, keep pathname + search; else leave as-is
			const u = new URL(url, window.location.origin);
			target = u.pathname + u.search;
		} catch {}

		for (const apiKey of Object.keys(apis)) {
			const cfg = apis[apiKey as keyof typeof apis];
			if (!cfg?.baseUrl) continue;
			// Match when target starts with baseUrl (both absolute and relative)
			if (target.startsWith(cfg.baseUrl) || url.startsWith(cfg.baseUrl)) {
				return cfg;
			}
		}
		return null;
	}
}
